import { type ReactNode, useId, useState } from "react";
import type { ClaimFamily, NewCategoryRequest } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { FieldLabel } from "@/components/ui/FieldLabel";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Refusal } from "./ChainControls";

const FAMILIES: readonly (readonly [ClaimFamily, string])[] = [
  ["product", "Product: its dependents narrow a thing"],
  ["method", "Method: its dependents narrow steps"],
];

/** One field of the house modal: its sentence-case label over the control it names. */
function Field({
  label,
  optional = false,
  children,
}: {
  label: string;
  optional?: boolean;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div>
      <FieldLabel htmlFor={id} look="sentence" optional={optional}>
        {label}
      </FieldLabel>
      {children(id)}
    </div>
  );
}

/** The name, description and family, handed to the write that makes the category. */
function CategoryForm({
  onCreate,
  pending,
  error,
  onClose,
}: {
  onCreate: (category: NewCategoryRequest) => void;
  pending: boolean;
  error: unknown;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [family, setFamily] = useState<ClaimFamily>("product");
  return (
    <form
      className="grid gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        onCreate({
          name: name.trim(),
          description: description.trim() || null,
          family,
        });
      }}
    >
      <Field label="Category name">
        {(id) => (
          <Input
            id={id}
            size="roomy"
            placeholder="e.g. Method of calibration"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        )}
      </Field>
      <Field label="Description" optional>
        {(id) => (
          <Input
            id={id}
            size="roomy"
            placeholder="e.g. How the emitter is tuned after assembly"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        )}
      </Field>
      <Field label="Family">
        {(id) => (
          <Select
            id={id}
            size="roomy"
            value={family}
            onChange={(event) => setFamily(event.target.value as ClaimFamily)}
          >
            {FAMILIES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {error != null && <Refusal error={error} />}
      <DialogFooter>
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={name.trim() === "" || pending}>
          Add category
        </Button>
      </DialogFooter>
    </form>
  );
}

/**
 * A claim category the catalogue does not offer, named by the attorney, with
 * the family its chains belong to so a dependent never crosses from one to the
 * other. The design asks for a name and a description; the family is the
 * server's question, asked the same way under them. The caller makes it, with
 * the retag it is for, in one write.
 */
export function NewCategoryDialog({
  open,
  onCreate,
  pending,
  error,
  onClose,
}: {
  open: boolean;
  onCreate: (category: NewCategoryRequest) => void;
  pending: boolean;
  error: unknown;
  onClose: () => void;
}) {
  const sentenceId = useId();
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent aria-describedby={sentenceId}>
        <div>
          <DialogTitle>New claim category</DialogTitle>
          <DialogDescription id={sentenceId} className="mt-1.5">
            Name the category you want to claim in. The description is optional
            and only helps you and your colleagues recognise it later.
          </DialogDescription>
        </div>
        <CategoryForm
          onCreate={onCreate}
          pending={pending}
          error={error}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}
